import { useEffect, useState } from 'react'
import type { CurrentQuickBooksGlPage, CurrentQuickBooksGlOrigin, QuickBooksCfoStatementsRenderContractResponse, GetCurrentQuickBooksGlOptions, ServerApiClient } from '../../../data/ServerApiClient'

export type CurrentGlScope = { start?: string; end?: string; accountId?: string; unavailable?: string; expectedOrigin?: CurrentQuickBooksGlOrigin }

/** Capture proof together with the original render, never from bootstrap. */
export function currentGlOriginFromStatements(contract: QuickBooksCfoStatementsRenderContractResponse): CurrentQuickBooksGlOrigin | null {
  const source = contract.current_source
  const original = contract.profit_and_loss
  if (!source?.source_identity || !source.configuration_identity || !source.source_job_id
    || !Number.isSafeInteger(source.source_revision) || source.source_revision < 1
    || !contract.workbook_id || !contract.connection_id || !contract.realm_id || !original.layout_version_id
    || !original.periods.length || original.periods.length > 120
    || [contract.profit_and_loss, contract.balance_sheet, contract.cash_flow].some((statement) =>
      statement.connection_id !== contract.connection_id || statement.layout_version_id !== original.layout_version_id
      || JSON.stringify(statement.periods) !== JSON.stringify(original.periods))) return null
  return {
    workbook_id: contract.workbook_id, connection_id: contract.connection_id, realm_id: contract.realm_id,
    layout_version_id: original.layout_version_id, configuration_identity: source.configuration_identity,
    native_revision: source.source_revision, source_job_id: source.source_job_id,
    source_identity: source.source_identity, periods: [...original.periods],
  }
}

type Props = {
  appApi: ServerApiClient
  workbookId: string | null
  initialScope?: CurrentGlScope
  fixedScope?: boolean
}

/** One bounded, provider-free GL reader used by desktop, mobile and drilldown. */
export function CfoCurrentGlDetail({ appApi, workbookId, initialScope = {}, fixedScope = false }: Props) {
  // Reset synchronously on primitive origin changes. An object-identity
  // effect would loop for callers passing a new empty scope each render.
  const scopeKey = JSON.stringify([workbookId, initialScope.start || '', initialScope.end || '', initialScope.accountId || '', initialScope.unavailable || '', initialScope.expectedOrigin || null])
  return <CurrentGlDetailBody key={scopeKey} appApi={appApi} workbookId={workbookId} initialScope={initialScope} fixedScope={fixedScope} />
}

function CurrentGlDetailBody({ appApi, workbookId, initialScope = {}, fixedScope = false }: Props) {
  const [query, setQuery] = useState<GetCurrentQuickBooksGlOptions>(() => ({
    start: initialScope.start, end: initialScope.end, accountId: initialScope.accountId, expectedOrigin: initialScope.expectedOrigin, limit: 250,
  }))
  const [dates, setDates] = useState({ start: initialScope.start || '', end: initialScope.end || '' })
  const [history, setHistory] = useState<GetCurrentQuickBooksGlOptions[]>([])
  const [page, setPage] = useState<CurrentQuickBooksGlPage | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!workbookId || initialScope.unavailable) return
    const controller = new AbortController()
    setLoading(true)
    setError('')
    setPage(null)
    appApi.getCurrentQuickBooksGl(workbookId, { ...query, signal: controller.signal })
      .then((next) => {
        if (controller.signal.aborted) return
        if (next.contract !== 'fg.current_native_gl_page.v1' || next.workbook_id !== workbookId
          || next.accounting_basis !== 'accrual' || next.account_id !== (query.accountId || null)
          || (query.start && next.start !== query.start) || (query.end && next.end !== query.end)
          || (query.expectedOrigin && (next.connection_id !== query.expectedOrigin.connection_id
            || next.realm_id !== query.expectedOrigin.realm_id || next.layout_version_id !== query.expectedOrigin.layout_version_id
            || next.configuration_identity !== query.expectedOrigin.configuration_identity
            || next.native_revision !== query.expectedOrigin.native_revision || next.source_job_id !== query.expectedOrigin.source_job_id))) {
          throw new Error('The returned ledger scope changed. Start a fresh query.')
        }
        setPage(next)
        setDates({ start: next.start, end: next.end })
      })
      .catch((failure: unknown) => {
        if (!controller.signal.aborted) setError(failure instanceof Error ? failure.message : 'Ledger detail could not be read.')
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false) })
    return () => controller.abort()
  }, [appApi, workbookId, query, initialScope.unavailable])

  const freshQuery = (next: GetCurrentQuickBooksGlOptions) => {
    setHistory([])
    setQuery({ ...next, expectedOrigin: initialScope.expectedOrigin, limit: 250, cursor: undefined })
  }
  const nextPage = () => {
    if (!page?.next_cursor) return
    setHistory((previous) => [...previous, query])
    setQuery({ ...query, start: page.start, end: page.end, cursor: page.next_cursor })
  }
  const previousPage = () => {
    const previous = history[history.length - 1]
    if (!previous) return
    setHistory((all) => all.slice(0, -1))
    setQuery(previous)
  }

  return <section className="cfo-current-gl-detail" aria-label="Current ledger detail">
    <p>Accrual ledger detail from your latest completed QuickBooks refresh. Ledger reconciliation and cash-basis detail are unavailable.</p>
    {!fixedScope && <form className="cfo-control-row" onSubmit={(event) => {
      event.preventDefault()
      if (!dates.start || !dates.end || dates.start > dates.end) {
        setError('Choose a valid start and end date.'); return
      }
      freshQuery({ start: dates.start, end: dates.end, accountId: query.accountId })
    }}>
      <label>From <input aria-label="Ledger start date" type="date" value={dates.start} onChange={(event) => setDates({ ...dates, start: event.target.value })} /></label>
      <label>Through <input aria-label="Ledger end date" type="date" value={dates.end} onChange={(event) => setDates({ ...dates, end: event.target.value })} /></label>
      <button type="submit" className="btn" disabled={loading}>Apply dates</button>
      {query.accountId && <button type="button" className="btn" onClick={() => freshQuery({ start: query.start, end: query.end })}>All accounts</button>}
    </form>}
    {initialScope.unavailable ? <p role="status">{initialScope.unavailable}</p> : !workbookId ? <p role="status">Select a connected workbook.</p> : null}
    {loading && <p role="status">Reading ledger detail…</p>}
    {error && <p role="alert">{error}</p>}
    {!initialScope.unavailable && workbookId && <button type="button" className="btn" disabled={loading} onClick={() => freshQuery(query)}>Reload detail</button>}
    {page && <>
      <p>{page.total_lines.toLocaleString()} dated lines in {page.start} through {page.end}. Updated {new Date(page.cdc_observed_through).toLocaleString()}.</p>
      {page.has_undated_controls && <p>Opening balances and other undated controls are retained separately and excluded from these dated lines.</p>}
      <div className="cfo-financials-gl-modal-table"><table className="cfo-gl-table">
        <thead><tr><th>Date</th><th>Type</th><th>Account</th><th>Memo</th><th>Debit ({page.home_currency})</th><th>Credit ({page.home_currency})</th><th>Amount ({page.home_currency})</th></tr></thead>
        <tbody>{page.rows.length ? page.rows.map((row, index) => <tr key={`${row.transaction_id}:${row.account_id}:${row.identity_occurrence}:${index}`}>
          <td>{row.date}</td><td>{row.transaction_type}</td>
          <td>{fixedScope || row.account_id === null ? String(row.fields.Account || row.account_id || '—') : <button type="button" className="btn" onClick={() => freshQuery({ start: page.start, end: page.end, accountId: row.account_id! })}>{String(row.fields.Account || row.account_id)}</button>}</td>
          <td>{typeof row.fields.Memo === 'string' ? row.fields.Memo : ''}</td>
          <td className="num">{row.debit ?? '—'}</td><td className="num">{row.credit ?? '—'}</td><td className="num">{row.amount ?? '—'}</td>
        </tr>) : <tr><td colSpan={7}>No dated ledger lines match this query.</td></tr>}</tbody>
      </table></div>
      <nav aria-label="Ledger pages"><button type="button" className="btn" disabled={loading || !history.length} onClick={previousPage}>Previous page</button>
        <span> Page {history.length + 1} </span>
        <button type="button" className="btn" disabled={loading || !page.next_cursor} onClick={nextPage}>Next page</button></nav>
    </>}
  </section>
}
